import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  Popconfirm,
  Space,
  Row,
  Col,
  Divider,
  Typography,
  Upload,
  Image,
  Select,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  GlobalOutlined,
  BankOutlined,
  TeamOutlined,
  ShopOutlined,
  UploadOutlined,
  GiftOutlined,
  EyeOutlined,
  InboxOutlined,
  ApartmentOutlined,
  FilterOutlined
} from '@ant-design/icons';
import settingsService from '../services/settingsService';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

// Helper function to compress and convert image to base64 (target ~300KB)
const compressImage = (file, maxSizeKB = 300) => {
  return new Promise((resolve, reject) => {
    // For GIFs, we can't easily compress without losing animation, so just read as-is if small enough
    if (file.type === 'image/gif') {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new window.Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate scaling to reduce dimensions if image is very large
        const maxDimension = 800;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start with high quality and reduce until under target size
        let quality = 0.9;
        let result = canvas.toDataURL('image/jpeg', quality);
        
        // Iteratively reduce quality until file is under target size
        while (result.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) { // 1.37 accounts for base64 overhead
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }
        
        resolve(result);
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

const BusinessUnitSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingGifId, setDeletingGifId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(''); // 'region', 'group', 'industry', 'country'
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  // Group Owner state
  const [ownerModalVisible, setOwnerModalVisible] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null); // null = adding new
  const [ownerForm] = Form.useForm();
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assigningOwner, setAssigningOwner] = useState(null);
  const [assignForm] = Form.useForm();

  // Dept Group Preset state
  const [presetModalVisible, setPresetModalVisible] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetGroups, setPresetGroups] = useState([]); // groups being edited in modal
  const [presetForm] = Form.useForm();

  // Load settings data
  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await settingsService.getSettings();
      
      if (result.success) {
        setSettings(result.data.data);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error('Load settings error:', error);
      message.error('Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // ── Group Owner handlers ───────────────────────────────────────────────
  const openOwnerModal = (owner = null) => {
    setEditingOwner(owner);
    ownerForm.resetFields();
    if (owner) ownerForm.setFieldsValue({ name: owner.name });
    setOwnerModalVisible(true);
  };

  const handleOwnerSave = async (values) => {
    let result;
    if (editingOwner) {
      result = await settingsService.updateGroupOwner(editingOwner.name, values.name);
    } else {
      result = await settingsService.addGroupOwner(values.name);
    }
    if (result.success) {
      message.success(`Group owner ${editingOwner ? 'updated' : 'added'} successfully`);
      setOwnerModalVisible(false);
      ownerForm.resetFields();
      loadSettings();
    } else {
      message.error(result.message);
    }
  };

  const handleOwnerDelete = async (ownerName) => {
    const result = await settingsService.removeGroupOwner(ownerName);
    if (result.success) { message.success('Group owner removed'); loadSettings(); }
    else message.error(result.message);
  };

  const openAssignModal = (owner) => {
    setAssigningOwner(owner);
    assignForm.resetFields();
    setAssignModalVisible(true);
  };

  const handleAssignMg = async (values) => {
    const names = Array.isArray(values.managingGroupNames) ? values.managingGroupNames : [values.managingGroupNames];
    const result = await settingsService.assignManagingGroupToOwner(assigningOwner.name, names);
    if (result.success) {
      message.success(`${names.length} managing group${names.length > 1 ? 's' : ''} assigned`);
      setAssignModalVisible(false);
      assignForm.resetFields();
      loadSettings();
    } else {
      message.error(result.message);
    }
  };

  const handleRemoveMgFromOwner = async (ownerName, mgName) => {
    const result = await settingsService.removeManagingGroupFromOwner(ownerName, mgName);
    if (result.success) { message.success('Managing group unlinked'); loadSettings(); }
    else message.error(result.message);
  };

  // ── Dept Group Preset handlers ────────────────────────────────────────
  const openPresetModal = (preset = null) => {
    setEditingPreset(preset);
    setPresetGroups(preset ? preset.groups.map(g => ({ ...g })) : []);
    presetForm.resetFields();
    if (preset) presetForm.setFieldsValue({ name: preset.name });
    setPresetModalVisible(true);
  };

  const handlePresetGroupLabelChange = (idx, label) => {
    const updated = [...presetGroups];
    updated[idx] = { ...updated[idx], label, key: label.toLowerCase().replace(/[^a-z0-9]+/g, '_') };
    setPresetGroups(updated);
  };

  const handlePresetGroupDeptChange = (idx, dept, checked) => {
    const updated = [...presetGroups];
    const depts = updated[idx].departments || [];
    updated[idx] = { ...updated[idx], departments: checked ? [...depts, dept] : depts.filter(d => d !== dept) };
    setPresetGroups(updated);
  };

  const handleAddPresetGroup = () => {
    setPresetGroups(prev => [...prev, { key: `group_${Date.now()}`, label: '', departments: [] }]);
  };

  const handleRemovePresetGroup = (idx) => {
    setPresetGroups(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePresetSave = async (values) => {
    if (!presetGroups.length) { message.error('Add at least one group'); return; }
    if (presetGroups.some(g => !g.label.trim())) { message.error('All groups must have a label'); return; }
    let result;
    if (editingPreset) {
      result = await settingsService.updateDepartmentGroupPreset(editingPreset.name, values.name, presetGroups);
    } else {
      result = await settingsService.addDepartmentGroupPreset(values.name, presetGroups);
    }
    if (result.success) {
      message.success(`Preset ${editingPreset ? 'updated' : 'created'} successfully`);
      setPresetModalVisible(false);
      presetForm.resetFields();
      loadSettings();
    } else {
      message.error(result.message);
    }
  };

  const handlePresetDelete = async (presetName) => {
    const result = await settingsService.removeDepartmentGroupPreset(presetName);
    if (result.success) { message.success('Preset deleted'); loadSettings(); }
    else message.error(result.message);
  };

  // Handle opening modal for adding/editing
  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setModalVisible(true);
    
    if (item) {
      form.setFieldsValue({
        name: item
      });
    } else {
      form.resetFields();
    }
  };

  // Handle saving
  const handleSave = async (values) => {
    try {
      let result;

      if (modalType === 'region') {
        if (editingItem) {
          result = await settingsService.updateRegion(editingItem, values.name);
        } else {
          result = await settingsService.addRegion(values.name);
        }
      } else if (modalType === 'group') {
        if (editingItem) {
          result = await settingsService.updateManagingGroup(editingItem, values.name);
        } else {
          result = await settingsService.addManagingGroup(values.name);
        }
      } else if (modalType === 'industry') {
        if (editingItem) {
          result = await settingsService.updateIndustry(editingItem, values.name);
        } else {
          result = await settingsService.addIndustry(values.name);
        }
      } else if (modalType === 'country') {
        if (editingItem) {
          result = await settingsService.updateCountry(editingItem, values.name);
        } else {
          result = await settingsService.addCountry(values.name);
        }
      }

      if (result.success) {
        message.success(`${modalType} ${editingItem ? 'updated' : 'added'} successfully`);
        setModalVisible(false);
        form.resetFields();
        loadSettings();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error('Save error:', error);
      message.error(`Error ${editingItem ? 'updating' : 'adding'} ${modalType}`);
    }
  };

  // Handle delete
  const handleDelete = async (type, item) => {
    try {
      let result;
      
      if (type === 'region') {
        result = await settingsService.removeRegion(item);
      } else if (type === 'group') {
        result = await settingsService.removeManagingGroup(item);
      } else if (type === 'industry') {
        result = await settingsService.removeIndustry(item);
      } else if (type === 'country') {
        result = await settingsService.removeCountry(item);
      }

      if (result.success) {
        message.success(`${type} deleted successfully`);
        loadSettings();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error('Delete error:', error);
      message.error(`Error deleting ${type}`);
    }
  };

  // Region columns - now simple strings
  const regionColumns = [
    {
      title: 'Region Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => record // record is just a string now
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal('region', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this region?"
            onConfirm={() => handleDelete('region', record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Managing Group columns - now simple strings
  const groupColumns = [
    {
      title: 'Group Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => record // record is just a string now
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal('group', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this managing group?"
            onConfirm={() => handleDelete('group', record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Industry columns - now same format as regions and groups
  const industryColumns = [
    {
      title: 'Industry Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => record // record is just a string now
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal('industry', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this industry?"
            onConfirm={() => handleDelete('industry', record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Country columns - now same format as regions and groups
  const countryColumns = [
    {
      title: 'Country Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => record // record is just a string now
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal('country', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this country?"
            onConfirm={() => handleDelete('country', record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title="Organisation Settings"
        subtitle="Manage your organisation's settings, regions, and quiz customisations"
        breadcrumbs={['Dashboard', 'Settings', 'Organisation Settings']}
      />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Tabs defaultActiveKey="celebration">
              {/* Regions Tab */}
              <TabPane 
                tab={
                  <span>
                    <GlobalOutlined />
                    Regions
                  </span>
                } 
                key="regions"
              >
                <div style={{ marginBottom: 16 }}>
                  <Button
                   
                    icon={<PlusOutlined />}
                    onClick={() => openModal('region')}
                  >
                    Add Region
                  </Button>
                </div>
                <Table
                  columns={regionColumns}
                  dataSource={settings?.regions || []}
                  rowKey={(record, index) => index}
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                />
              </TabPane>

              {/* Managing Groups Tab */}
              <TabPane 
                tab={
                  <span>
                    <TeamOutlined />
                    Managing Groups
                  </span>
                } 
                key="groups"
              >
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type=""
                    icon={<PlusOutlined />}
                    onClick={() => openModal('group')}
                  >
                    Add Managing Group
                  </Button>
                </div>
                <Table
                  columns={groupColumns}
                  dataSource={settings?.managingGroups || []}
                  rowKey={(record, index) => index}
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                />
              </TabPane>

              {/* Group Owners Tab */}
              <TabPane
                tab={
                  <span>
                    <ApartmentOutlined />
                    Group Owners
                  </span>
                }
                key="groupOwners"
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Group Owners are top-level entities (e.g. "KFC South Africa") that own one or more Managing Groups. Use this to filter reports at the owner level.
                </Text>
                <div style={{ marginBottom: 16 }}>
                  <Button icon={<PlusOutlined />} onClick={() => openOwnerModal()}>
                    Add Group Owner
                  </Button>
                </div>
                <Table
                  columns={[
                    {
                      title: 'Group Owner',
                      dataIndex: 'name',
                      key: 'name',
                      render: (name) => <strong>{name}</strong>
                    },
                    {
                      title: 'Managing Groups',
                      key: 'managingGroups',
                      render: (_, owner) => (
                        <Space wrap>
                          {(owner.managingGroups || []).map(mg => (
                            <Tag
                              key={mg}
                              closable
                              onClose={() => handleRemoveMgFromOwner(owner.name, mg)}
                            >
                              {mg}
                            </Tag>
                          ))}
                          <Button
                            size="small"
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => openAssignModal(owner)}
                          >
                            Assign
                          </Button>
                        </Space>
                      )
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_, owner) => (
                        <Space>
                          <Button type="text" icon={<EditOutlined />} onClick={() => openOwnerModal(owner)}>Edit</Button>
                          <Popconfirm
                            title={`Remove "${owner.name}" as a group owner? This does not delete the managing groups.`}
                            onConfirm={() => handleOwnerDelete(owner.name)}
                            okText="Yes" cancelText="No"
                          >
                            <Button type="text" danger icon={<DeleteOutlined />}>Delete</Button>
                          </Popconfirm>
                        </Space>
                      )
                    }
                  ]}
                  dataSource={settings?.groupOwners || []}
                  rowKey="name"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  locale={{ emptyText: 'No group owners defined yet' }}
                />
              </TabPane>

              {/* Department Group Presets Tab */}
              <TabPane
                tab={<span><SettingOutlined /> Dept Group Presets</span>}
                key="deptGroupPresets"
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Create reusable department grouping presets. When generating a report, pick a preset to instantly apply the groups instead of configuring from scratch each time.
                </Text>
                <div style={{ marginBottom: 16 }}>
                  <Button icon={<PlusOutlined />} onClick={() => openPresetModal()}>New Preset</Button>
                </div>
                <Table
                  columns={[
                    {
                      title: 'Preset Name',
                      dataIndex: 'name',
                      key: 'name',
                      render: n => <strong>{n}</strong>,
                      width: 220
                    },
                    {
                      title: 'Groups',
                      key: 'groups',
                      render: (_, preset) => (
                        <Space wrap>
                          {(preset.groups || []).map(g => (
                            <Tag key={g.key} color="blue">
                              {g.label} ({(g.departments || []).length})
                            </Tag>
                          ))}
                        </Space>
                      )
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_, preset) => (
                        <Space>
                          <Button type="text" icon={<EditOutlined />} onClick={() => openPresetModal(preset)}>Edit</Button>
                          <Popconfirm
                            title={`Delete preset "${preset.name}"?`}
                            onConfirm={() => handlePresetDelete(preset.name)}
                            okText="Yes" cancelText="No"
                          >
                            <Button type="text" danger icon={<DeleteOutlined />}>Delete</Button>
                          </Popconfirm>
                        </Space>
                      )
                    }
                  ]}
                  dataSource={settings?.departmentGroupPresets || []}
                  rowKey="name"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  locale={{ emptyText: 'No presets created yet' }}
                />
              </TabPane>

              {/* Industries Tab */}}
              <TabPane 
                tab={
                  <span>
                    <BankOutlined />
                    Industries
                  </span>
                } 
                key="industries"
              >
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type=""
                    icon={<PlusOutlined />}
                    onClick={() => openModal('industry')}
                  >
                    Add Industry
                  </Button>
                </div>
                <Table
                  columns={industryColumns}
                  dataSource={settings?.industries || []}
                  rowKey={(record, index) => index}
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                />
              </TabPane>

              {/* Countries Tab */}
              <TabPane 
                tab={
                  <span>
                    <ShopOutlined />
                    Countries
                  </span>
                } 
                key="countries"
              >
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type=""
                    icon={<PlusOutlined />}
                    onClick={() => openModal('country')}
                  >
                    Add Country
                  </Button>
                </div>
                <Table
                  columns={countryColumns}
                  dataSource={settings?.countries || []}
                  rowKey={(record, index) => index}
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                />
              </TabPane>

              {/* Celebration GIFs Tab */}
              <TabPane 
                tab={
                  <span>
                    <GiftOutlined />
                    Celebration GIFs
                  </span>
                } 
                key="celebration"
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Upload fun GIF images that will be randomly displayed on the quiz results page above "Nicely done!" to celebrate quiz completion. These will be available for all quizzes. Max file size: 20MB.
                </Text>
                <Upload
                  accept="image/gif,image/*"
                  showUploadList={false}
                  multiple
                  disabled={uploading}
                  customRequest={async ({ file, onSuccess, onError }) => {
                    try {
                      setUploading(true);
                      
                      // Check file size (max 20MB)
                      const maxSize = 20 * 1024 * 1024;
                      if (file.size > maxSize) {
                        message.error(`File too large. Maximum size is 20MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
                        onError?.(new Error('File too large'));
                        return;
                      }

                      // For GIFs, upload to server filesystem (don't compress to preserve animation)
                      // For other images, compress first
                      let base64Data;
                      if (file.type === 'image/gif') {
                        // Read GIF as-is
                        base64Data = await new Promise((resolve, reject) => {
                          const reader = new FileReader();
                          reader.readAsDataURL(file);
                          reader.onload = () => resolve(reader.result);
                          reader.onerror = reject;
                        });
                      } else {
                        // Compress other images
                        base64Data = await compressImage(file, 500);
                      }

                      // Upload to server filesystem
                      const uploadResult = await settingsService.uploadCelebrationGif(base64Data, file.name);
                      if (!uploadResult.success) {
                        message.error(uploadResult.message);
                        onError?.(new Error(uploadResult.message));
                        return;
                      }

                      // Save reference to settings (URL, not base64)
                      const gifData = {
                        id: `gif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        url: uploadResult.data.url, // Server file path, not base64
                        name: file.name,
                        filename: uploadResult.data.filename // Store filename for deletion
                      };
                      const result = await settingsService.addCelebrationGif(gifData);
                      if (result.success) {
                        message.success('Celebration image added!');
                        loadSettings();
                        onSuccess?.();
                      } else {
                        // If saving to settings failed, delete the uploaded file
                        await settingsService.deleteUploadedGif(uploadResult.data.filename);
                        message.error(result.message);
                        onError?.(new Error(result.message));
                      }
                    } catch (error) {
                      console.error('Upload error:', error);
                      message.error('Failed to upload image');
                      onError?.(error);
                    } finally {
                      setUploading(false);
                    }
                  }}
                >
                  <Button icon={<UploadOutlined />} type="dashed" style={{ marginBottom: 16 }} loading={uploading}>
                    {uploading ? 'Uploading...' : 'Upload Celebration GIF/Image'}
                  </Button>
                </Upload>
                
                {settings?.celebrationGifs && settings.celebrationGifs.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {settings.celebrationGifs.map((gif) => (
                      <Col key={gif.id} xs={12} sm={8} md={6} lg={4}>
                        <div style={{
                          position: 'relative',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          <img
                            src={gif.url}
                            alt={gif.name || 'Celebration GIF'}
                            style={{
                              width: '100%',
                              height: '120px',
                              objectFit: 'cover',
                              display: 'block',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              Modal.info({
                                title: gif.name || 'Celebration Image',
                                content: <img src={gif.url} alt="Preview" style={{ width: '100%' }} />,
                                width: 600,
                                maskClosable: true,
                                okText: 'Close'
                              });
                            }}
                          />
                          <Popconfirm
                            title="Delete this image?"
                            onConfirm={async () => {
                              setDeletingGifId(gif.id);
                              try {
                                // Delete file from server if filename exists
                                if (gif.filename) {
                                  await settingsService.deleteUploadedGif(gif.filename);
                                }
                                // Remove from settings
                                const result = await settingsService.removeCelebrationGif(gif.id);
                                if (result.success) {
                                  message.success('Image removed');
                                  loadSettings();
                                } else {
                                  message.error(result.message);
                                }
                              } finally {
                                setDeletingGifId(null);
                              }
                            }}
                            okText="Yes"
                            cancelText="No"
                            disabled={deletingGifId === gif.id}
                          >
                            <Button
                              type="primary"
                              danger
                              icon={<DeleteOutlined />}
                              size="small"
                              loading={deletingGifId === gif.id}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                minWidth: '28px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            />
                          </Popconfirm>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    border: '2px dashed #d9d9d9',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa'
                  }}>
                    <InboxOutlined style={{ fontSize: '48px', color: '#bfbfbf', marginBottom: '8px' }} />
                    <Text type="secondary" style={{ display: 'block' }}>
                      No celebration images uploaded yet
                    </Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                      Upload GIFs to show random celebrations on all quiz results pages
                    </Text>
                  </div>
                )}
              </TabPane>

              {/* Targeting Filters Tab */}
              <TabPane
                tab={
                  <span>
                    <FilterOutlined />
                    Targeting Filters
                  </span>
                }
                key="targeting-filters"
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Saved targeting filters can be loaded in the Advanced Targeting modal when creating campaigns.
                </Text>
                <Table
                  columns={[
                    {
                      title: 'Name',
                      dataIndex: 'name',
                      key: 'name',
                      render: name => <Text strong>{name}</Text>
                    },
                    {
                      title: 'Keywords',
                      dataIndex: 'keywords',
                      key: 'keywords',
                      render: kws => kws?.length ? kws.map(k => <Tag key={k} color="blue">{k}</Tag>) : <Text type="secondary">—</Text>
                    },
                    {
                      title: 'Match Mode',
                      dataIndex: 'matchMode',
                      key: 'matchMode',
                      width: 110,
                      render: m => <Tag>{m || 'word'}</Tag>
                    },
                    {
                      title: 'Business Units',
                      dataIndex: 'businessUnits',
                      key: 'businessUnits',
                      render: bus => bus?.length ? bus.map(b => <Tag key={b}>{b}</Tag>) : <Text type="secondary">—</Text>
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: 100,
                      render: (_, record) => (
                        <Space>
                          <Popconfirm
                            title="Delete this targeting filter?"
                            onConfirm={async () => {
                              const result = await settingsService.removeTargetingFilter(record.name);
                              if (result.success) {
                                message.success('Filter deleted');
                                loadSettings();
                              } else {
                                message.error(result.message);
                              }
                            }}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button type="text" danger icon={<DeleteOutlined />}>Delete</Button>
                          </Popconfirm>
                        </Space>
                      )
                    }
                  ]}
                  dataSource={settings?.targetingFilters || []}
                  rowKey="name"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  locale={{ emptyText: 'No targeting filters saved yet. Save filters from the Advanced Targeting modal when creating a campaign.' }}
                />
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>

      {/* Dept Group Preset Modal */}
      <Modal
        title={editingPreset ? `Edit Preset — ${editingPreset.name}` : 'New Department Group Preset'}
        open={presetModalVisible}
        onOk={() => presetForm.submit()}
        onCancel={() => { setPresetModalVisible(false); presetForm.resetFields(); setPresetGroups([]); }}
        okType="default"
        okText={editingPreset ? 'Update' : 'Create'}
        width={800}
        style={{ top: 30 }}
      >
        <Form form={presetForm} layout="vertical" onFinish={handlePresetSave}>
          <Form.Item name="name" label="Preset Name" rules={[{ required: true, message: 'Enter a name' }]}>
            <Input placeholder="e.g. MBSA Standard" style={{ maxWidth: 320 }} />
          </Form.Item>
        </Form>
        <Divider orientation="left" plain>Groups</Divider>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {presetGroups.map((group, idx) => (
            <Card
              key={group.key}
              size="small"
              title={
                <Input
                  placeholder="Group label (e.g. Sales & Marketing)"
                  value={group.label}
                  onChange={e => handlePresetGroupLabelChange(idx, e.target.value)}
                  style={{ maxWidth: 280 }}
                />
              }
              extra={
                <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => handleRemovePresetGroup(idx)} />
              }
              style={{ backgroundColor: '#fafafa' }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>Select departments for this group (these are the departments found in the current report):</Text>
              <div style={{ marginTop: 8, maxHeight: 160, overflowY: 'auto' }}>
                {(settings?.allKnownDepartments || []).length > 0 ? (
                  <Row gutter={[8, 4]}>
                    {(settings.allKnownDepartments || []).map(dept => (
                      <Col key={dept} span={8}>
                        <input
                          type="checkbox"
                          checked={(group.departments || []).includes(dept)}
                          onChange={e => handlePresetGroupDeptChange(idx, dept, e.target.checked)}
                          style={{ marginRight: 4 }}
                        />
                        <Text style={{ fontSize: 12 }}>{dept}</Text>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>Department keywords will be matched when the preset is applied to a report. You can leave this empty and departments will auto-detect from the preset group labels.</Text>
                )}
              </div>
            </Card>
          ))}
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddPresetGroup} block>
            Add Group
          </Button>
        </Space>
      </Modal>

      {/* Group Owner Add/Edit Modal */}
      <Modal
        title={editingOwner ? 'Edit Group Owner' : 'Add Group Owner'}
        open={ownerModalVisible}
        onOk={() => ownerForm.submit()}
        onCancel={() => { setOwnerModalVisible(false); ownerForm.resetFields(); }}
        okType="default"
        okText={editingOwner ? 'Update' : 'Add'}
      >
        <Form form={ownerForm} layout="vertical" onFinish={handleOwnerSave}>
          <Form.Item name="name" label="Group Owner Name" rules={[{ required: true, message: 'Please enter a name' }]}>
            <Input placeholder="e.g. KFC South Africa" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Managing Group to Owner Modal */}
      <Modal
        title={`Assign Managing Group to "${assigningOwner?.name}"`}
        open={assignModalVisible}
        onOk={() => assignForm.submit()}
        onCancel={() => { setAssignModalVisible(false); assignForm.resetFields(); }}
        okType="default"
        okText="Assign"
      >
        <Form form={assignForm} layout="vertical" onFinish={handleAssignMg}>
          <Form.Item name="managingGroupNames" label="Managing Groups" rules={[{ required: true, message: 'Please select at least one managing group' }]}>
            <Select
              mode="multiple"
              placeholder="Select managing groups..."
              showSearch
              maxTagCount="responsive"
              options={(settings?.managingGroups || []).filter(g => !assigningOwner?.managingGroups?.includes(g)).map(g => ({ label: g, value: g }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal for Add/Edit */}
      <Modal
        title={`${editingItem ? 'Edit' : 'Add'} ${modalType.charAt(0).toUpperCase() + modalType.slice(1)}`}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okType='default'
        okText={editingItem ? 'Update' : 'Add'}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder={`Enter ${modalType} name`} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BusinessUnitSettings;